// src/components/InfoPanel.tsx
import React from 'react';
import { Question } from '../types';
import './InfoPanel.css';

interface InfoPanelProps {
  question: Question | null;
  isOpen: boolean;
  onClose: () => void;
}

export const InfoPanel: React.FC<InfoPanelProps> = ({ question, isOpen, onClose }) => {
  if (!isOpen || !question || !question.tooltipContent) {
    return null;
  }

  const content = question.tooltipContent;
  const orgSize = localStorage.getItem('organizationSize') || 'small';

  return (
    <div className="info-panel-overlay">
      <div className={`info-panel ${isOpen ? 'open' : 'closed'}`}>
        <div className="info-panel-header">
          <h2 className="info-panel-title">Question Details</h2>
          <button className="info-panel-close" onClick={onClose} aria-label="Close info panel">
            ✕
          </button>
        </div>

        <div className="info-panel-content">
          <section className="info-section">
            <h3 className="section-title">Question</h3>
            <p className="question-text">{question.question}</p>
          </section>

          <section className="info-section">
            <h3 className="section-title">What This Means</h3>
            <p className="plain-text">{content.plainEnglish}</p>
          </section>

          {content.hipaaReference && (
            <section className="info-section">
              <h3 className="section-title">HIPAA Requirement</h3>
              <div className="regulation-box">
                <code className="regulation-ref">{content.hipaaReference}</code>
              </div>
            </section>
          )}

          {content.realWorldExample && (
            <section className="info-section">
              <h3 className="section-title">Real-World Example</h3>
              <div className="example-box">
                <p>{content.realWorldExample}</p>
              </div>
            </section>
          )}

          {content.implementationSteps && (
            <section className="info-section">
              <h3 className="section-title">How to Implement</h3>
              <ol className="steps-list">
                {content.implementationSteps.map((step, idx) => (
                  <li key={idx} className="step-item">{step}</li>
                ))}
              </ol>
            </section>
          )}

          {content.commonMistakes && (
            <section className="info-section">
              <h3 className="section-title">Common Mistakes to Avoid</h3>
              <ul className="mistakes-list">
                {content.commonMistakes.map((mistake, idx) => (
                  <li key={idx} className="mistake-item">
                    <span className="mistake-icon">⚠️</span>
                    {mistake}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {content.estimatedCost && content.timeToComplete && (
            <section className="info-section">
              <div className="cost-timeline-grid">
                <div className="cost-box">
                  <h4>Estimated Cost</h4>
                  <p className="cost-value">
                    {orgSize === 'small' ? content.estimatedCost.small :
                     orgSize === 'medium' ? content.estimatedCost.medium :
                     content.estimatedCost.large}
                  </p>
                </div>
                <div className="timeline-box">
                  <h4>Time to Complete</h4>
                  <p className="timeline-value">
                    {orgSize === 'small' ? content.timeToComplete.small :
                     orgSize === 'medium' ? content.timeToComplete.medium :
                     content.timeToComplete.large}
                  </p>
                </div>
              </div>
            </section>
          )}

          {content.resourceLinks && (
            <section className="info-section">
              <h3 className="section-title">Resources</h3>
              <div className="resources-list">
                {content.resourceLinks.map((link, idx) => (
                  <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className={`resource-link resource-${link.type}`}>
                    <span className="resource-icon">
                      {link.type === 'regulation' && '📋'}
                      {link.type === 'guide' && '📖'}
                      {link.type === 'tool' && '🛠️'}
                      {link.type === 'vendor' && '🔗'}
                    </span>
                    <span className="resource-title">{link.title}</span>
                    <span className="resource-arrow">→</span>
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
